import api from './api';

/**
 * Customer-acquisition tracking: campaign attribution, first-party funnel
 * events, and the consent-gated Reddit Pixel.
 *
 * Two separate layers:
 *  - First-party (always on): an anonymous per-tab session id and the UTM
 *    tags the visit arrived with, kept in sessionStorage and sent to our own
 *    API. No cookies, nothing shared with a third party.
 *  - Reddit Pixel (consent only): never loaded, and so sets no cookies and
 *    sends nothing, until the visitor accepts advertising cookies.
 *
 * Never pass personal data (names, emails, postcodes, order/tracking numbers,
 * case descriptions) into anything in this file.
 */

const PIXEL_ID = process.env.REACT_APP_REDDIT_PIXEL_ID || '';

const CONSENT_KEY = 'pb_ad_consent';
const SESSION_KEY = 'pb_session_id';
const ATTRIBUTION_KEY = 'pb_attribution';
const STARTED_KEY = 'pb_report_started';
const CONVERTED_KEY = 'pb_reported_conversions';

const UTM_PARAMS = ['source', 'medium', 'campaign', 'content', 'term'];

const store = (storage) => ({
  get(key) {
    try {
      return storage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key, value) {
    try {
      storage.setItem(key, value);
    } catch {
      /* storage blocked — tracking degrades, the site doesn't */
    }
  }
});

const session = store(typeof window !== 'undefined' ? window.sessionStorage : null);
const local = store(typeof window !== 'undefined' ? window.localStorage : null);

const readJson = (s, key, fallback) => {
  try {
    return JSON.parse(s.get(key)) ?? fallback;
  } catch {
    return fallback;
  }
};

/* ── Consent ──────────────────────────────────────────────────────────────── */

const consentListeners = new Set();

/** { state: 'granted' | 'denied' | 'unset', at } */
export const getConsent = () => readJson(local, CONSENT_KEY, null) || { state: 'unset', at: null };

export const onConsentChange = (fn) => {
  consentListeners.add(fn);
  return () => consentListeners.delete(fn);
};

/** Ask the banner to show again (footer "Cookie settings" link). */
export const reopenConsent = () => consentListeners.forEach((fn) => fn({ reopen: true }));

const REDDIT_COOKIES = ['_rdt_uuid', '_rdt_cid', '_rdt_em'];

export const setConsent = (state) => {
  const previous = getConsent().state;
  local.set(CONSENT_KEY, JSON.stringify({ state, at: new Date().toISOString() }));

  if (state === 'granted') {
    loadRedditPixel();
    trackPixelPageVisit(true);
  } else if (previous === 'granted') {
    // Withdrawn: clear the Pixel's first-party cookies, then reload so the
    // already-running Pixel script is gone too.
    const host = window.location.hostname;
    const domains = ['', host, `.${host}`, `.${host.split('.').slice(-2).join('.')}`];
    REDDIT_COOKIES.forEach((name) =>
      domains.forEach((domain) => {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain ? `; domain=${domain}` : ''}`;
      })
    );
    window.location.reload();
    return;
  }
  consentListeners.forEach((fn) => fn({ state }));
};

export const isPixelConfigured = () => Boolean(PIXEL_ID);

const hasAdConsent = () => getConsent().state === 'granted';

/* ── Reddit Pixel ─────────────────────────────────────────────────────────── */

let pixelLoaded = false;

/** Official Reddit Pixel base code, injected only after consent. */
export const loadRedditPixel = () => {
  if (pixelLoaded || !PIXEL_ID || !hasAdConsent()) return;
  pixelLoaded = true;
  /* eslint-disable */
  !function(w,d){if(!w.rdt){var p=w.rdt=function(){p.sendEvent?p.sendEvent.apply(p,arguments):p.callQueue.push(arguments)};p.callQueue=[];var t=d.createElement("script");t.src="https://www.redditstatic.com/ads/pixel.js",t.async=!0;var s=d.getElementsByTagName("script")[0];s.parentNode.insertBefore(t,s)}}(window,document);
  /* eslint-enable */
  window.rdt('init', PIXEL_ID, { optOut: false, useDecimalCurrencyValues: true });
};

const rdt = (...args) => {
  if (!PIXEL_ID || !hasAdConsent()) return;
  loadRedditPixel();
  window.rdt(...args);
};

let lastPixelPath = null;

/** PageVisit once per SPA navigation (not per re-render or StrictMode double-run). */
const trackPixelPageVisit = (force = false) => {
  const path = window.location.pathname;
  if (!force && path === lastPixelPath) return;
  lastPixelPath = path;
  rdt('track', 'PageVisit');
};

/* ── Attribution ──────────────────────────────────────────────────────────── */

export const getSessionId = () => {
  let id = session.get(SESSION_KEY);
  if (!id) {
    id =
      window.crypto?.randomUUID?.() ||
      `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
    session.set(SESSION_KEY, id);
  }
  return id;
};

/** Token-like path segments (magic links etc.) never leave the browser. */
const safePath = (pathname) =>
  pathname
    .split('/')
    .map((segment) => (/^[A-Za-z0-9_-]{20,}$/.test(segment) ? ':token' : segment))
    .join('/');

const externalReferrer = () => {
  try {
    const ref = document.referrer ? new URL(document.referrer) : null;
    return ref && ref.hostname !== window.location.hostname ? ref.hostname : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Reads UTM tags / Reddit click id from the current URL. A tagged landing
 * becomes the latest touch (and the first touch if this session has none);
 * an untagged page leaves attribution untouched, so the tags survive the
 * whole journey to the report form.
 */
const captureAttribution = () => {
  const params = new URLSearchParams(window.location.search);
  const touch = {};
  UTM_PARAMS.forEach((key) => {
    const value = params.get(`utm_${key}`);
    if (value) touch[key] = value.slice(0, 120);
  });
  const rdtCid = params.get('rdt_cid');
  if (rdtCid) touch.rdtCid = rdtCid.slice(0, 200);

  const current = readJson(session, ATTRIBUTION_KEY, {});
  if (!Object.keys(touch).length) {
    // First page of an untagged visit: remember where it landed, as direct.
    if (!current.firstTouch) {
      const landing = { landingPage: safePath(window.location.pathname), referrer: externalReferrer(), at: new Date().toISOString() };
      session.set(ATTRIBUTION_KEY, JSON.stringify({ firstTouch: landing, lastTouch: landing }));
    }
    return;
  }

  touch.landingPage = safePath(window.location.pathname);
  touch.referrer = externalReferrer();
  touch.at = new Date().toISOString();
  session.set(
    ATTRIBUTION_KEY,
    JSON.stringify({ firstTouch: current.firstTouch?.source ? current.firstTouch : touch, lastTouch: touch })
  );
};

/** Attached to the case on submission — stored alongside it, never sent to Reddit. */
export const getAcquisitionPayload = () => ({
  sessionId: getSessionId(),
  ...readJson(session, ATTRIBUTION_KEY, {}),
  consent: getConsent()
});

const sendEvent = (event, extra = {}) => {
  api
    .post('/api/tracking/events', { event, ...getAcquisitionPayload(), ...extra })
    .catch(() => {});
};

/* ── Public events ────────────────────────────────────────────────────────── */

let lastViewedPath = null;

/** Called on every public-site route change (Layout). */
export const trackPageView = () => {
  captureAttribution();
  const path = safePath(window.location.pathname);
  if (path === lastViewedPath) return;
  lastViewedPath = path;
  sendEvent('page_viewed', { path });
  trackPixelPageVisit();
};

/** First real interaction with the report form — once per session. */
export const trackReportStarted = () => {
  if (session.get(STARTED_KEY)) return;
  session.set(STARTED_KEY, '1');
  sendEvent('report_started');
  rdt('track', 'Custom', { customEventName: 'ReportStarted' });
};

/**
 * Only after the backend has created the case. `conversionId` is a random id
 * the server generated for this case (not the case id); Reddit uses it to
 * deduplicate, and we also skip it locally if it was already sent.
 */
export const trackReportSubmitted = (conversionId) => {
  if (!conversionId) return;
  const sent = readJson(session, CONVERTED_KEY, []);
  if (sent.includes(conversionId)) return;
  session.set(CONVERTED_KEY, JSON.stringify([...sent, conversionId]));
  rdt('track', 'Lead', { conversionId });
};

/** Load the Pixel on startup when consent was given on an earlier visit. */
export const initTracking = () => {
  loadRedditPixel();
};
